'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, cn, HoverCard, HoverCardContent, HoverCardTrigger } from '@newfront-insurance/core-ui/v2';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { type ComponentProps, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '../ui/carousel';
export type InlineCitationProps = ComponentProps<'span'>;
export function InlineCitation(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group inline items-center gap-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export type InlineCitationTextProps = ComponentProps<'span'>;
export function InlineCitationText(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("group-hover:bg-accent transition-colors", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export type InlineCitationCardProps = ComponentProps<typeof HoverCard>;
export function InlineCitationCard(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <HoverCard closeDelay={0} openDelay={0} {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export type InlineCitationCardTriggerProps = ComponentProps<typeof Badge> & {
  sources: string[];
};
export function InlineCitationCardTrigger(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let sources;
  if ($[0] !== t0) {
    ({
      sources,
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = sources;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    sources = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("ml-1 rounded-full", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== sources) {
    t2 = children ?? (sources.length ? <>{new URL(sources[0]).hostname} {sources.length > 1 && `+${sources.length - 1}`}</> : "unknown");
    $[7] = children;
    $[8] = sources;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <HoverCardTrigger asChild={true}><Badge className={t1} variant="secondary" {...props}>{t2}</Badge></HoverCardTrigger>;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
export type InlineCitationCardBodyProps = ComponentProps<typeof HoverCardContent>;
export function InlineCitationCardBody(t0) {
  const $ = _c(12);
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      collisionPadding: t1,
      align: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = t2;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    t2 = $[4];
  }
  const collisionPadding = t1 === undefined ? 16 : t1;
  const align = t2 === undefined ? "start" : t2;
  let t3;
  if ($[5] !== className) {
    t3 = cn("relative w-80 p-0", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== align || $[8] !== collisionPadding || $[9] !== props || $[10] !== t3) {
    t4 = <HoverCardContent className={t3} collisionPadding={collisionPadding} align={align} {...props} />;
    $[7] = align;
    $[8] = collisionPadding;
    $[9] = props;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
const CarouselApiContext = createContext<CarouselApi | undefined>(undefined);
const useCarouselApi = () => {
  const context = useContext(CarouselApiContext);
  return context;
};
export type InlineCitationCarouselProps = ComponentProps<typeof Carousel>;
export function InlineCitationCarousel(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const [api, setApi] = useState();
  let t1;
  if ($[4] !== className) {
    t1 = cn("w-full", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <Carousel className={t1} setApi={setApi} {...props}>{children}</Carousel>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== api || $[11] !== t2) {
    t3 = <CarouselApiContext.Provider value={api}>{t2}</CarouselApiContext.Provider>;
    $[10] = api;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
}
export type InlineCitationCarouselContentProps = ComponentProps<'div'>;
export function InlineCitationCarouselContent(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <CarouselContent {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export type InlineCitationCarouselItemProps = ComponentProps<'div'>;
export function InlineCitationCarouselItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full space-y-2 p-4 pl-8", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <CarouselItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export type InlineCitationCarouselHeaderProps = ComponentProps<'div'>;
export function InlineCitationCarouselHeader(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("bg-secondary flex items-center justify-between gap-2 rounded-t-md p-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export type InlineCitationCarouselIndexProps = ComponentProps<'div'>;
export function InlineCitationCarouselIndex(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const api = useCarouselApi();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  let t1;
  let t2;
  if ($[4] !== api) {
    t1 = () => {
      if (!api) {
        return;
      }
      setCount(api.scrollSnapList().length);
      setCurrent(api.selectedScrollSnap() + 1);
      api.on("select", () => {
        setCurrent(api.selectedScrollSnap() + 1);
      });
    };
    t2 = [api];
    $[4] = api;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== className) {
    t3 = cn("text-muted-foreground flex flex-1 items-center justify-end px-3 py-1 text-xs", className);
    $[7] = className;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = children ?? `${current}/${count}`;
  let t5;
  if ($[9] !== props || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className={t3} {...props}>{t4}</div>;
    $[9] = props;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}
export type InlineCitationCarouselPrevProps = ComponentProps<'button'>;
export function InlineCitationCarouselPrev(t0) {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const api = useCarouselApi();
  let t1;
  if ($[3] !== api) {
    t1 = () => {
      if (api) {
        api.scrollPrev();
      }
    };
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleClick = t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("shrink-0", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowLeftIcon className="text-muted-foreground size-4" />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleClick || $[9] !== props || $[10] !== t2) {
    t4 = <button aria-label="Previous" className={t2} onClick={handleClick} type="button" {...props}>{t3}</button>;
    $[8] = handleClick;
    $[9] = props;
    $[10] = t2;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export type InlineCitationCarouselNextProps = ComponentProps<'button'>;
export function InlineCitationCarouselNext(t0) {
  const $ = _c(12);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  const api = useCarouselApi();
  let t1;
  if ($[3] !== api) {
    t1 = () => {
      if (api) {
        api.scrollNext();
      }
    };
    $[3] = api;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleClick = t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("shrink-0", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowRightIcon className="text-muted-foreground size-4" />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleClick || $[9] !== props || $[10] !== t2) {
    t4 = <button aria-label="Next" className={t2} onClick={handleClick} type="button" {...props}>{t3}</button>;
    $[8] = handleClick;
    $[9] = props;
    $[10] = t2;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
export type InlineCitationSourceProps = ComponentProps<'div'> & {
  title?: string;
  url?: string;
  description?: string;
};
export function InlineCitationSource(t0) {
  const $ = _c(18);
  let children;
  let className;
  let description;
  let props;
  let title;
  if ($[0] !== t0) {
    const {
      title: t1,
      url,
      description: t2,
      className: t3,
      children: t4,
      ...t5
    } = t0;
    title = t1;
    description = t2;
    className = t3;
    children = t4;
    props = t5;
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = description;
    $[4] = props;
    $[5] = title;
  } else {
    children = $[1];
    className = $[2];
    description = $[3];
    props = $[4];
    title = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn("relative space-y-1.5", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== title) {
    t2 = title && <h4 className="pr-6 text-base leading-tight font-bold break-words">{title}</h4>;
    $[8] = title;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== description) {
    t3 = description && <p className="line-clamp-3 text-sm leading-relaxed">"{description}"</p>;
    $[10] = description;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== children || $[13] !== props || $[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t4 = <div className={t1} {...props}>{t2}{t3}{children}</div>;
    $[12] = children;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
}
export type InlineCitationQuoteProps = ComponentProps<'blockquote'>;
export function InlineCitationQuote(t0) {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("border-muted text-muted-foreground border-l-2 pl-3 text-sm italic", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <blockquote className={t1} {...props}>{children}</blockquote>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}